// The one currency every screen that adds up money across deals reports in.
//
// A deal, a term line and an invoice keep the currency they were written in,
// and their own pages show it. Anything that sums across deals — the
// dashboard, the reports, the calendar, the platform costs — is converted by
// the server into ONE currency, and this is where that currency is chosen.
// It used to be chosen per screen, each with its own default, so a reader who
// switched the dashboard to USD opened the margin page in CAD.
//
// It is a per-reader convenience, remembered in this browser: who wants to
// read in USD is a fact about the reader, not about the data. Storage that
// throws or comes back empty just means CAD.

import { Currency } from "@crm/shared";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

/** CAD, the server's default too: most deals are CAD and the rates are the Bank of Canada's. */
export const DEFAULT_REPORT_CURRENCY: Currency = "CAD";

export const REPORT_CURRENCY_STORAGE_KEY = "crm.reportCurrency";

/** The stored choice, or the default when there is none or storage refuses. */
export function readStoredReportCurrency(): Currency {
  try {
    const parsed = Currency.safeParse(window.localStorage.getItem(REPORT_CURRENCY_STORAGE_KEY));
    return parsed.success ? parsed.data : DEFAULT_REPORT_CURRENCY;
  } catch {
    return DEFAULT_REPORT_CURRENCY;
  }
}

function storeReportCurrency(currency: Currency): void {
  try {
    window.localStorage.setItem(REPORT_CURRENCY_STORAGE_KEY, currency);
  } catch {
    // The choice lives for this page and no longer.
  }
}

/**
 * Screens whose OWN search param is called `currency` and means something else:
 * on the deals list it is a filter ("the USD deals"), which a reader in CAD
 * still asks for. The switch never touches those.
 */
export const PATHS_OWNING_CURRENCY_PARAM: ReadonlySet<string> = new Set(["/deals"]);

/**
 * The report currency a link names (`?currency=USD`, as every report's link
 * used to carry), or null when it names none, names something that is not a
 * currency, or is on a screen whose `currency` param means something else.
 */
export function linkedReportCurrency(location: {
  pathname: string;
  search: Readonly<Record<string, unknown>>;
}): Currency | null {
  if (PATHS_OWNING_CURRENCY_PARAM.has(location.pathname)) return null;
  if (!("currency" in location.search)) return null;
  const parsed = Currency.safeParse(location.search["currency"]);
  return parsed.success ? parsed.data : null;
}

interface ReportCurrencyContextValue {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
}

// The default is a working value rather than a throw, so a component rendered
// outside the app (a unit test of one card) reads CAD like a first visit does.
const ReportCurrencyContext = createContext<ReportCurrencyContextValue>({
  currency: DEFAULT_REPORT_CURRENCY,
  setCurrency: () => {},
});

export function ReportCurrencyProvider({
  children,
  initial,
}: {
  children: ReactNode;
  /**
   * The currency the landing link names, if any. Starting in it, rather than
   * in the stored one and switching once the shell has read the link, keeps a
   * shared `?currency=USD` link from first asking every report on the page in
   * the wrong currency: a computed report the server works out in full and the
   * browser then throws away.
   */
  initial?: Currency | null;
}): ReactNode {
  const [currency, setState] = useState<Currency>(() => initial ?? readStoredReportCurrency());
  const setCurrency = useCallback((next: Currency): void => {
    setState(next);
    storeReportCurrency(next);
  }, []);
  const value = useMemo(() => ({ currency, setCurrency }), [currency, setCurrency]);
  return <ReportCurrencyContext.Provider value={value}>{children}</ReportCurrencyContext.Provider>;
}

/** The currency an aggregate screen asks the server for. */
export function useReportCurrency(): Currency {
  return useContext(ReportCurrencyContext).currency;
}

/** For the one control that changes it, and the link that names one. */
export function useSetReportCurrency(): (currency: Currency) => void {
  return useContext(ReportCurrencyContext).setCurrency;
}
